import { useQuery } from '@tanstack/react-query';
import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { Button, Card, ErrorText, Field, Input, Spinner, Textarea } from '../lib/ui.tsx';
import { SolfoTile } from '../components/Logo.tsx';

/** A2 · Students join exclusively through a coach invite. */
export default function JoinInvite() {
  const { token } = useParams<{ token: string }>();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', dateOfBirth: '', goals: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<{ coachName: string; email: string | null }>(`/api/auth/invites/${token}/preview`),
    retry: false,
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string }>('/api/auth/accept-invite', {
        body: { ...form, token, email: form.email || preview.data?.email },
      });
      await login(res.token);
      navigate('/app/range-test'); // A7 · guided range test on first login
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (preview.isLoading) return <Spinner />;
  if (preview.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-4">
        <Card className="max-w-sm text-center">
          <p className="text-sm text-slate-600">This invite link is invalid or was already used.</p>
          <p className="mt-2 text-xs text-slate-400">Ask your coach for a fresh one.</p>
        </Card>
      </div>
    );
  }

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-3 flex justify-center">
          <SolfoTile size={64} />
        </div>
        <p className="mb-6 text-center text-sm text-slate-600">
          <strong>{preview.data?.coachName}</strong> invited you to practice together on Solfo.
        </p>
        <Card>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Your name">
              <Input value={form.name} onChange={set('name')} required autoFocus />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email || preview.data?.email || ''} onChange={set('email')} required />
            </Field>
            <Field label="Password">
              <Input type="password" value={form.password} onChange={set('password')} required minLength={8} />
            </Field>
            <Field label="Date of birth" hint="You must be 18 or older to use Solfo.">
              <Input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} required />
            </Field>
            <Field label="What do you want to achieve?" hint="In your own words — your coach will see this.">
              <Textarea rows={2} value={form.goals} onChange={set('goals')} />
            </Field>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? '…' : 'Join your coach'}
            </Button>
            <ErrorText error={error} />
          </form>
        </Card>
      </div>
    </div>
  );
}
